import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { Article, Plus, Trash, PencilSimple, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Farmhouses", "Resorts", "Banquet Halls", "Function Halls", "Guides"];
const empty = { title: "", category: "Guides", author: "Team Avinya", excerpt: "", content: "", featured_image: "", seo_title: "", meta_description: "", status: "draft" };

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    if (!f.title) return toast.error("Title is required.");
    try {
      if (editing) await api.patch(`/admin/blog/${editing.id}`, f);
      else await api.post("/admin/blog", f);
      toast.success(editing ? "Article updated" : "Article created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async (p) => { await api.patch(`/admin/blog/${p.id}`, { status: p.status === "published" ? "draft" : "published" }); load(); };
  const del = async (p) => { await api.delete(`/admin/blog/${p.id}`); load(); };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setF((prev) => ({ ...prev, featured_image: r.data.path })); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Blog & Guides</h1>
        <Button data-testid="blog-new" onClick={openNew} className="gap-1 rounded-full bg-primary hover:bg-green-900"><Plus size={16} /> New article</Button>
      </div>
      <p className="mt-1 text-sm text-stone-500">Write helpful, honest guides. Articles stay as drafts until you publish them.</p>

      <div className="mt-5 space-y-3">
        {posts === null ? null : posts.length === 0 ? (
          <EmptyState icon={Article} title="No articles yet" message="Create your first guide, e.g. 'Best Farmhouses in Hyderabad for Birthdays'." />
        ) : posts.map((p) => (
          <div key={p.id} data-testid={`blog-${p.id}`} className="flex items-center gap-4 rounded-2xl border border-stone-200 bg-white p-4">
            {p.featured_image ? <img src={fileUrl(p.featured_image)} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" /> : <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg bg-stone-100"><Article size={20} className="text-stone-400" /></div>}
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading font-semibold text-stone-900">{p.title}</p>
              <p className="text-xs text-stone-400">{p.category} · /{p.slug} · <span className={p.status === "published" ? "text-green-600" : "text-amber-600"}>{p.status}</span></p>
            </div>
            <button data-testid={`blog-publish-${p.id}`} onClick={() => togglePublish(p)} title={p.status === "published" ? "Unpublish" : "Publish"} className="rounded-full border border-stone-200 p-2 text-stone-600 hover:bg-stone-50">{p.status === "published" ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
            <button onClick={() => openEdit(p)} className="rounded-full border border-stone-200 p-2 text-stone-600 hover:bg-stone-50"><PencilSimple size={16} /></button>
            <button onClick={() => del(p)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-stone-500">Title</Label><Input data-testid="blog-title" value={f.title} onChange={set("title")} className="mt-1 rounded-xl" placeholder="Best Farmhouses in Hyderabad for Birthdays" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-stone-500">Category</Label>
                <Select value={f.category} onValueChange={set("category")}><SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs text-stone-500">Author</Label><Input value={f.author} onChange={set("author")} className="mt-1 rounded-xl" /></div>
            </div>
            <div><Label className="text-xs text-stone-500">Excerpt</Label><Textarea value={f.excerpt} onChange={set("excerpt")} className="mt-1 rounded-xl" placeholder="Short summary shown on the blog list" /></div>
            <div><Label className="text-xs text-stone-500">Content</Label><Textarea data-testid="blog-content" value={f.content} onChange={set("content")} className="mt-1 min-h-[220px] rounded-xl" placeholder="Write your article. Separate paragraphs with a blank line." /></div>
            <div>
              <Label className="text-xs text-stone-500">Featured image</Label>
              <div className="mt-1 flex items-center gap-3">
                <Input value={f.featured_image} onChange={set("featured_image")} className="rounded-xl" placeholder="Paste image URL or upload" />
                <label className="cursor-pointer whitespace-nowrap rounded-xl border border-stone-200 px-3 py-2 text-sm hover:bg-stone-50">Upload<input type="file" accept="image/*" className="hidden" onChange={uploadImg} /></label>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label className="text-xs text-stone-500">SEO title (optional)</Label><Input value={f.seo_title} onChange={set("seo_title")} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs text-stone-500">Meta description (optional)</Label><Input value={f.meta_description} onChange={set("meta_description")} className="mt-1 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setF({ ...f, status: "draft" }); }} className="rounded-xl hidden" />
            <Button data-testid="blog-save" onClick={save} className="rounded-xl bg-primary hover:bg-green-900">{editing ? "Save changes" : "Create draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
